import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { CalendarIcon } from '@phosphor-icons/react';
import { format, parse, isValid } from 'date-fns';
import { useState, useEffect, useRef } from 'react';
import { T } from 'gt-react';

type CalendarProps = React.ComponentProps<typeof Calendar>;
type SingleDatePickerProps = Extract<CalendarProps, { mode: 'single' }>;

const DATE_FORMAT = 'MM/dd/yyyy';

/**
 * Attempts to parse a date string in various common formats.
 * Returns a valid Date or null if parsing fails.
 */
function parseFlexibleDate(input: string): Date | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Try common date formats
  const formats = [
    'MM/dd/yyyy',
    'M/d/yyyy',
    'MM-dd-yyyy',
    'M-d-yyyy',
    'yyyy-MM-dd',
    'MM/dd/yy',
    'M/d/yy',
  ];

  for (const fmt of formats) {
    const parsed = parse(trimmed, fmt, new Date());
    if (isValid(parsed)) {
      // Sanity check: year should be reasonable (1900-2100)
      const year = parsed.getFullYear();
      if (year >= 1900 && year <= 2100) {
        return parsed;
      }
    }
  }

  return null;
}

export function DatePicker({
  id,
  value,
  className,
  placeholder = 'MM/DD/YYYY',
  onChange,
  onSelect,
  onValidityChange,
  disabled,
  fromDate,
  toDate,
}: {
  /** Forwarded to the text input so a `<Label htmlFor>` can name the field. */
  id?: string;
  value: Date | undefined;
  className?: string;
  placeholder?: string;
  /** Preferred callback for date changes */
  onChange?: (date: Date | undefined) => void;
  /** @deprecated Use onChange instead. Kept for backward compatibility. */
  onSelect?: (date: Date | undefined) => void;
  onValidityChange?: (isValid: boolean) => void;
  disabled?: SingleDatePickerProps['disabled'];
  fromDate?: Date;
  toDate?: Date;
}) {
  // Support both onChange and onSelect for backward compatibility
  const handleDateChange = (date: Date | undefined) => {
    onChange?.(date);
    onSelect?.(date);
  };
  const markValid = (isValid: boolean) => {
    setHasError(!isValid);
    onValidityChange?.(isValid);
  };
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(
    value ? format(value, DATE_FORMAT) : '',
  );
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value ? format(value, DATE_FORMAT) : '');
    setHasError(false);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    markValid(true);
  };

  const handleInputBlur = () => {
    if (!inputValue.trim()) {
      markValid(true);
      handleDateChange(undefined);
      return;
    }

    const parsed = parseFlexibleDate(inputValue);
    if (parsed) {
      setInputValue(format(parsed, DATE_FORMAT));
      markValid(true);
      handleDateChange(parsed);
    } else {
      markValid(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInputBlur();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setInputValue(value ? format(value, DATE_FORMAT) : '');
      markValid(true);
      inputRef.current?.blur();
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      setInputValue(format(date, DATE_FORMAT));
      markValid(true);
    } else {
      setInputValue('');
    }
    handleDateChange(date);
    setIsOpen(false);
  };

  return (
    <div className={cn('relative flex w-[140px] items-center', className)}>
      <Input
        ref={inputRef}
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={cn(
          'h-8 pr-8 text-sm',
          hasError && 'border-destructive focus-visible:ring-destructive',
        )}
      />
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 h-8 w-8 border-l border-input hover:bg-muted"
            type="button"
          >
            <CalendarIcon className="h-4 w-4 text-primary" />
            <span className="sr-only">
              <T context="screen reader label for calendar picker button">
                Open calendar
              </T>
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={value}
            defaultMonth={value || new Date()}
            onSelect={handleCalendarSelect}
            disabled={disabled}
            fromDate={fromDate}
            toDate={toDate}
            required
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
